// A TATTOO CONVENTION'S ARTIST LIST (2026-10-18; docs/CONVENTIONS.md, the API's consolidated_api/convention.py).
//
// A show is a page like any shop's whose row carries one more block, `convention: {name, from, to}`, and whose roster is
// the show's: up to 600 artists from dozens of shops, where a shop's is at most 40. A <select> of 600 names on a phone is
// a scroll nobody finishes, so at a show the artist field is a SEARCH: the client types, the names that match come up
// under the box, a tap puts one in it, and a name the list does not have is taken exactly as typed. What is POSTED is what
// a shop's picker posts (artists.ts withArtistPicked): `nameOfArtist`, the name, with `artistId` beside it only when the
// name came off the list. Nothing here decides anything else about the form: the show's state is the row's `state`, so
// the questions, the notices and the record are that state's, as for a shop.
//
// The block reaches the form only on the API's own /shop answer and only while the API's FEATURE_CONVENTIONS is on (the
// static config file never carries it), so every shop's form, and a show's with the switch off, is the form it was. The
// decisions that are pure live here so they have tests of their own (convention.test.ts).

import type { Lang } from "./i18n";
import { SOMEONE_ELSE, type ArtistOption } from "./artists";

export type Convention = { name: string; from: string; to: string };

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** The show this page is, or null: the block as the API's /shop answer carries it, read only when it is whole. */
export function conventionOf(client: unknown): Convention | null {
  const raw = (client as { convention?: unknown } | null | undefined)?.convention as Record<string, unknown> | null | undefined;
  if (!raw || typeof raw !== "object") return null;
  const name = `${raw.name ?? ""}`.trim();
  const from = `${raw.from ?? ""}`.trim();
  const to = `${raw.to ?? ""}`.trim();
  if (!name || !DAY.test(from) || !DAY.test(to) || to < from) return null;
  return { name, from, to };
}

/** Does this page's artist field search the list: a show with a roster for this form. A show with nobody on the list for
 *  the service the client picked keeps the plain field, as a shop does. */
export const searchesArtists = (show: Convention | null, options: ArtistOption[]): boolean => !!show && options.length > 0;

/** A name as the search compares it: lower case, accents off their letters, anything that is not a letter or a digit a
 *  space, and single spaces. "José O'Brien" and "jose obrien" are one name to a client typing on a phone. */
export function fold(text: string): string {
  return `${text ?? ""}`
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/** The most names the list shows at once, under the box: a phone's screen of them. */
export const MATCHES_SHOWN = 8;

/** The names that match what was typed, best first, at most `limit`: a name that STARTS with it, then one with a WORD that
 *  starts with it ("lund" finds Kira Lund), then one that has it anywhere; in roster order within each. Nothing typed is no
 *  list: a show's roster is never dumped on a client who has not started. */
export function matchArtists(options: ArtistOption[], typed: string, limit: number = MATCHES_SHOWN): ArtistOption[] {
  const wanted = fold(typed);
  if (!wanted) return [];
  const starts: ArtistOption[] = [];
  const word: ArtistOption[] = [];
  const anywhere: ArtistOption[] = [];
  for (const option of options) {
    const name = fold(option.name);
    if (name.startsWith(wanted)) starts.push(option);
    else if (` ${name}`.includes(` ${wanted}`)) word.push(option);
    else if (name.includes(wanted)) anywhere.push(option);
  }
  return [...starts, ...word, ...anywhere].slice(0, limit);
}

/** The picker's choice for what is in the box: the list's own spelling when the box holds a name on it (any case, accents
 *  folded), "Someone else" for a name the list does not have, and nothing for an empty box. A name typed in full that is
 *  on the list is the same choice as a tap on it, so it carries the artist's id. */
export function choiceForTyped(options: ArtistOption[], typed: string): string {
  const wanted = fold(typed);
  if (!wanted) return "";
  const hit = options.find((a) => fold(a.name) === wanted);
  return hit ? hit.name : SOMEONE_ELSE;
}

/** The search's own words, in the form's register and both languages: a piercing says piercer, everything else artist
 *  (artists.ts pickerCopy's split). The Spanish awaits a native read. */
export function conventionCopy(
  service: "tattoo" | "piercing" | "cosmetic" | "",
  lang: Lang = "en"
): { placeholder: string; hint: string; notListed: string; missing: string; listLabel: string } {
  if (lang === "es") {
    const quien = service === "piercing" ? "perforador" : "artista";
    return {
      placeholder: `Escribe el nombre de tu ${quien}`,
      hint: `Elige a tu ${quien} de la lista mientras escribes. ¿No está? Escribe su nombre completo.`,
      notListed: "No está en la lista del evento. Usaremos el nombre tal como lo escribiste.",
      missing: `Escribe el nombre de tu ${quien}.`,
      listLabel: `${quien === "artista" ? "Artistas" : "Perforadores"} del evento`,
    };
  }
  const who = service === "piercing" ? "piercer" : "artist";
  return {
    placeholder: `Start typing your ${who}'s name`,
    hint: `Pick your ${who} from the list as you type. Not on it? Type their full name.`,
    notListed: "Not on the show's list. We'll use the name as you typed it.",
    missing: `Type your ${who}'s name.`,
    listLabel: `The show's ${who}s`,
  };
}
